/**
 * ══════════════════════════════════════════════════════════════════════════════
 * PTEL DESIGN SYSTEM v2.0 - InfoBanner Component
 * ══════════════════════════════════════════════════════════════════════════════
 *
 * Banner informativo con 3 variantes visuales para cada paso del flujo PTEL.
 *
 * Variantes:
 * - normaliza (teal): Información sobre conversión automática
 * - valida (amber): Advertencia sobre elementos que requieren revisión
 * - descarga (lime): Confirmación de datos listos para exportar
 *
 * Uso:
 *   <InfoBanner variant="normaliza">
 *     Se han normalizado 12 coordenadas automáticamente.
 *   </InfoBanner>
 *
 * ══════════════════════════════════════════════════════════════════════════════
 */

import { useState, type ReactNode } from 'react';
import { Lightbulb, AlertTriangle, CheckCircle, X } from 'lucide-react';
import { colors, transitions, borderRadius, spacing, typography } from '../design-tokens';

// ─────────────────────────────────────────────────────────────────────────────
// TIPOS
// ─────────────────────────────────────────────────────────────────────────────

export type InfoBannerVariant = 'normaliza' | 'valida' | 'descarga';

export interface InfoBannerProps {
  /** Variante visual del banner (normaliza/valida/descarga) */
  variant: InfoBannerVariant;
  /** Contenido del banner */
  children: ReactNode;
  /** Permite cerrar el banner (por defecto: true) */
  dismissible?: boolean;
  /** Callback cuando se cierra el banner */
  onDismiss?: () => void;
  /** Clases CSS adicionales */
  className?: string;
  /** ID para testing */
  testId?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// CONFIGURACIÓN DE VARIANTES
// ─────────────────────────────────────────────────────────────────────────────

const variantConfig = {
  normaliza: {
    color: colors.steps.normaliza,       // #2DD4BF (teal)
    bgAlpha: 'rgba(45, 212, 191, 0.08)',
    borderAlpha: 'rgba(45, 212, 191, 0.3)',
    Icon: Lightbulb,
    ariaLabel: 'Información de normalización',
  },
  valida: {
    color: colors.steps.valida,          // #FBBF24 (amber)
    bgAlpha: 'rgba(251, 191, 36, 0.08)',
    borderAlpha: 'rgba(251, 191, 36, 0.3)',
    Icon: AlertTriangle,
    ariaLabel: 'Advertencia de validación',
  },
  descarga: {
    color: colors.steps.descarga,        // #A3E635 (lime)
    bgAlpha: 'rgba(163, 230, 53, 0.08)',
    borderAlpha: 'rgba(163, 230, 53, 0.3)',
    Icon: CheckCircle,
    ariaLabel: 'Confirmación de descarga',
  },
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// COMPONENTE
// ─────────────────────────────────────────────────────────────────────────────

export function InfoBanner({
  variant,
  children,
  dismissible = true,
  onDismiss,
  className = '',
  testId,
}: InfoBannerProps) {
  const [isVisible, setIsVisible] = useState(true);
  const config = variantConfig[variant];
  const { Icon, color, bgAlpha, borderAlpha, ariaLabel } = config;

  const handleDismiss = () => {
    setIsVisible(false);
    onDismiss?.();
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div
      role="status"
      aria-label={ariaLabel}
      data-testid={testId ?? `info-banner-${variant}`}
      data-variant={variant}
      className={className}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: spacing[4],
        padding: `${spacing[4]} ${spacing[5]}`,
        backgroundColor: bgAlpha,
        border: `1px solid ${borderAlpha}`,
        borderRadius: borderRadius.lg,
        fontFamily: typography.fontFamily.sans,
        fontSize: typography.fontSize.base,
        color: colors.fg.secondary,
        transition: `opacity ${transitions.base}`,
      }}
    >
      {/* Icono */}
      <Icon
        size={18}
        style={{
          color,
          flexShrink: 0,
          marginTop: '2px',
        }}
        aria-hidden="true"
      />

      {/* Contenido */}
      <div
        style={{
          flex: 1,
          lineHeight: 1.5,
        }}
      >
        {children}
      </div>

      {/* Botón cerrar */}
      {dismissible && (
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Cerrar"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: spacing[1],
            background: 'transparent',
            border: 'none',
            borderRadius: borderRadius.sm,
            color: colors.fg.muted,
            cursor: 'pointer',
            transition: `all ${transitions.fast}`,
            flexShrink: 0,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = color;
            e.currentTarget.style.backgroundColor = `${color}1A`;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = colors.fg.muted;
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          <X size={16} aria-hidden="true" />
        </button>
      )}
    </div>
  );
}

export default InfoBanner;
